import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, TextField } from '../components/ui';
import { ApiClientError } from '../api/client';
import { fieldErrorsFromApiError } from '../lib/field-errors';
import logo from '../theme/NeuroEdgeLogo.png';
import { useAuth } from './AuthContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(email: string, password: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!EMAIL_RE.test(email.trim())) errors.email = 'Enter a valid email address.';
  if (password.length < 1) errors.password = 'Enter your password.';
  return errors;
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function clearFieldError(key: string) {
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate(email, password);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setBusy(true);
    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
        } else {
          setError(err.message);
        }
      } else {
        setError(
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not sign in. Please try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ne-auth">
      <Card className="ne-auth__card">
        <p className="ne-auth__back">
          <Link to="/">← Back to home</Link>
        </p>
        <img src={logo} alt="" className="ne-auth__logo" />
        <h1 className="ne-auth__title">Sign in</h1>
        <p className="ne-auth__subtitle">NeuroEdge Connect</p>
        <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError('email');
            }}
            error={fieldErrors.email}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearFieldError('password');
            }}
            error={fieldErrors.password}
          />
          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
        <p className="ne-auth__foot">
          <Link to="/forgot-password">Forgot password?</Link>
        </p>
        <p className="ne-auth__foot">
          New here? <Link to="/register">Create a workspace</Link> ·{' '}
          <Link to="/pricing">See plans &amp; pricing</Link>
        </p>
        <p className="ne-auth__foot">
          <Link to="/privacy">Privacy Policy</Link>
        </p>
      </Card>
    </div>
  );
}
